/**
 * V7 (VISTOERA_CANVAS_VIDEO_PLAYER_ARCHITECTURE_ROOT_CAUSE_AND_POSTER_FIRST_
 * ACTIVE_PLAYER_V7) — POSTER-FIRST / ACTIVE-PLAYER presentation state.
 * Pure helper — no media side effects.
 *
 * Deliberately shrunk from V6's 7-phase machine (PRIMING/READY/
 * STARTING_PLAYBACK/PLAYING/PAUSED/RESETTING_TO_FIRST_FRAME/ERROR) crossed
 * with 3 independent booleans (posterBroken/framePrimed/posterLoaded) — the
 * combinatorial surface that made V3-V6 so hard to reason about. §22's
 * target model:
 *
 *   IDLE_POSTER — nothing is playing. The canonical poster asset (or a
 *                 non-black loading/fallback surface if it hasn't painted
 *                 yet) is the ONLY visual. No <video> element is mounted
 *                 by the component in this phase — see CanvasVideoPreview.
 *   STARTING    — a <video> has just been mounted and asked to play; the
 *                 poster stays on top until a real decoded frame exists,
 *                 so the transition is poster -> video, never poster ->
 *                 black -> video.
 *   PLAYING     — the decoded <video> is the visual.
 *   PAUSED      — the decoded <video> stays the visual, at its own
 *                 currentTime — pausing must never fall back to the
 *                 poster or a placeholder (PAUSED_FRAME_INVARIANT).
 *   ERROR       — poster if one exists, otherwise an explicit non-black
 *                 error surface. Never a silent black box.
 *
 * There is no RESETTING_TO_FIRST_FRAME phase — `ended` goes straight back
 * to IDLE_POSTER (the poster already *is* the canonical first frame, so
 * there is nothing to seek-and-wait for).
 */

export type VideoPlayerPhase = 'IDLE_POSTER' | 'STARTING' | 'PLAYING' | 'PAUSED' | 'ERROR'

export interface VideoPresentationInput {
  phase: VideoPlayerPhase
  hasPoster: boolean
  posterBroken: boolean
  /** True once the <video> mounted for the active player has a real decoded frame. */
  videoDrawable: boolean
}

export interface VideoPresentationOutput {
  /** Mount the poster <img> (independent of whether it's painted yet — see posterLoaded). */
  showPoster: boolean
  /** Mount the <video> element at all. False in IDLE_POSTER — this is the whole V7 fix. */
  mountVideo: boolean
  /** <video> is the frontmost visible surface (only once it has a real frame). */
  showVideo: boolean
  showPlay: boolean
  /** Non-black animated loading/error surface — shown whenever neither poster nor video is available yet. */
  showLoadingPlaceholder: boolean
}

export function resolveVideoPresentationState(input: VideoPresentationInput): VideoPresentationOutput {
  const { phase, hasPoster, posterBroken, videoDrawable } = input
  const posterUsable = hasPoster && !posterBroken

  switch (phase) {
    case 'IDLE_POSTER':
      return {
        showPoster: posterUsable,
        mountVideo: false,
        showVideo: false,
        showPlay: true,
        showLoadingPlaceholder: !posterUsable,
      }
    case 'STARTING':
      return {
        // Keep the poster (or loading surface) on top until the video has a
        // real frame — never let the freshly-mounted, still-black t=0
        // <video> show through.
        showPoster: posterUsable && !videoDrawable,
        mountVideo: true,
        showVideo: videoDrawable,
        showPlay: !videoDrawable,
        showLoadingPlaceholder: !posterUsable && !videoDrawable,
      }
    case 'PLAYING':
      // V8: keep the center Play/Pause hit-target mounted so pause is an
      // explicit icon click — media body clicks are reserved for expand/select.
      return { showPoster: false, mountVideo: true, showVideo: true, showPlay: true, showLoadingPlaceholder: false }
    case 'PAUSED':
      return { showPoster: false, mountVideo: true, showVideo: true, showPlay: true, showLoadingPlaceholder: false }
    case 'ERROR':
      return {
        showPoster: posterUsable,
        mountVideo: false,
        showVideo: false,
        showPlay: posterUsable,
        showLoadingPlaceholder: !posterUsable,
      }
    default:
      return { showPoster: posterUsable, mountVideo: false, showVideo: false, showPlay: true, showLoadingPlaceholder: !posterUsable }
  }
}

/** COMPLETED_VIDEO_VISIBLE_SURFACE_INVARIANT (carried over from V6): at
 * least one real/explicit surface is always present — never neither. */
export function visibleSurfaceInvariantHolds(output: VideoPresentationOutput): boolean {
  return output.showPoster || output.showVideo || output.showLoadingPlaceholder
}

/**
 * `data-visible-surface` ground truth for Preview E2E — see V6's
 * computeVisibleSurface docstring for why "mounted with a usable src" is
 * NOT the same as "actually painted", and why this can never return
 * "black". `posterLoaded` is real `<img onLoad>` state, not `hasPoster`.
 */
export type VisibleSurface = 'video' | 'poster' | 'loading' | 'error'

export interface VisibleSurfaceInput {
  phase: VideoPlayerPhase
  showVideo: boolean
  showPoster: boolean
  posterLoaded: boolean
  posterBroken: boolean
}

export function computeVisibleSurface(input: VisibleSurfaceInput): VisibleSurface {
  const { phase, showVideo, showPoster, posterLoaded, posterBroken } = input
  if (showVideo) return 'video'
  if (showPoster && posterLoaded && !posterBroken) return 'poster'
  if (phase === 'ERROR' && !posterLoaded) return 'error'
  return 'loading'
}
